import { authService } from "@/services/authService";
import impersonacaoService from "@/services/impersonacaoService";
import { RoleUtils } from "@/services/roles/roles";
import Cookies from "js-cookie";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useCursoStore } from "./CursoStore";

/**
 * Erro lancado quando o backend recusa o logout por haver uma sessao de
 * "acessar como outro usuario" ativa (HTTP 409). Encerrar a sessao nesse
 * momento apagaria as credenciais da pessoa acessada em todos os
 * dispositivos dela; a saida correta e usar "voltar ao meu usuario".
 */
export class LogoutBloqueadoError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "LogoutBloqueadoError";
  }
}

/**
 * Lancado quando acessarComo()/voltarAoMeuUsuario() gravam o token novo mas
 * loadUser() falha ao recarregar a partir dele (ex.: soluco em /auth/me).
 * loadUser() engole o proprio erro e so limpa o estado local — sem este
 * relancamento, a acao "resolveria" mesmo com a sessao morta: nesse
 * instante o cookie apagado era a UNICA credencial viva do administrador
 * (entrar() ja apagou a antiga e encerrou a sessao web dele), entao a
 * pessoa cairia na tela de login sem explicacao e sem caminho de volta.
 */
export class RecargaDeSessaoFalhouError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RecargaDeSessaoFalhouError";
  }
}

/** Validade do cookie auth_token quando ele guarda a credencial temporaria
 * de impersonacao — tem que casar com ImpersonacaoService::DURACAO_EM_HORAS
 * (back/app/Services/ImpersonacaoService.php). Nao faz sentido o cookie no
 * navegador sobreviver mais tempo do que o token que ele representa. */
const DURACAO_TOKEN_IMPERSONACAO_HORAS = 2;

export interface AccountFlags {
  isAdmin: boolean;
  isAluno: boolean;
  isOrientador: boolean;
  isCoordenador: boolean;
  isSecretaria: boolean;
  isCoordenadorTcc: boolean;
  isUserNormal: boolean;
  isDesligado: boolean;
}

export interface AccountAluno {
  cpf: string | null;
  status: string | null;
  matricula: string | null;
}

export interface AccountProfessor {
  cpf: string | null;
  siape: string | null;
}

export interface AccountRoleDetail {
  id: number;
  name: string;
  label: string;
  description: string | null;
  permissions: string[];
}

export interface User {
  id: number;
  name: string;
  email: string;
  auth_mode: string;
  is_ldap: boolean;
  must_change_password: boolean;
  temporary_password_expires_at: string | null;
  roles: string[];
  permissions: string[];
  role_details: AccountRoleDetail[];
  flags: AccountFlags;
  aluno: AccountAluno | null;
  professor: AccountProfessor | null;
  acessando_como: { impersonador_id: number; impersonador_nome: string } | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  access_token: string;
  token_type: string;
  message: string;
}

export const useAuthStore = defineStore("auth", () => {
  const user = ref<User | null>(null);
  const token = ref<string | null>(Cookies.get("auth_token") || null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const errorCode = ref<string | null>(null);
  const initialized = ref(false);
  const activeRole = ref<string | null>(Cookies.get("active_role") || null);

  const isAuthenticated = computed(() => !!user.value && !!token.value);

  const isAdmin = computed(() => activeRole.value === "admin" || (hasRole("admin") && !activeRole.value));
  const isAluno = computed(() => activeRole.value === "aluno" || (hasRole("aluno") && !activeRole.value && !hasRole("orientador")));
  const isOrientador = computed(() => activeRole.value === "orientador" || (hasRole("orientador") && !activeRole.value));
  const isCoordenador = computed(() => activeRole.value === "coordenador" || (hasRole("coordenador") && !activeRole.value));
  const isSecretaria = computed(() => activeRole.value === "secretaria" || (hasRole("secretaria") && !activeRole.value));
  const isCoordenadorTcc = computed(() => activeRole.value === "coordenador_tcc" || (hasRole("coordenador_tcc") && !activeRole.value));
  const isUserNormal = computed(() => activeRole.value === "user_normal" || (hasRole("user_normal") && !activeRole.value && !hasRole("admin") && !hasRole("aluno") && !hasRole("orientador") && !hasRole("coordenador") && !hasRole("secretaria") && !hasRole("coordenador_tcc")));

  const availableRoles = computed(() => {
    if (!user.value?.roles) return [];

    return RoleUtils.sortByPriority(user.value.roles);
  });

  const hasMultipleRoles = computed(() => availableRoles.value.length > 1);

  const activeRoleLabel = computed(() => {
    const role = activeRole.value || getDefaultRole();
    return user.value?.role_details?.find((detail) => detail.name === role)?.label || RoleUtils.getLabel(role);
  });

  const acessandoComo = computed(() => !!user.value?.acessando_como);
  const nomeDoImpersonador = computed(() => user.value?.acessando_como?.impersonador_nome ?? null);

  function hasRole(role: string): boolean {
    return user.value?.roles?.includes(role) ?? false;
  }

  /**
   * Retorna a role padrão baseada na prioridade:
   * Admin > Coordenador > Secretária > Orientador > Aluno > user_normal
   */
  function getDefaultRole(): string {
    if (!user.value?.roles || user.value.roles.length === 0) {
      return "user_normal";
    }

    const defaultRole = RoleUtils.getDefaultRole(user.value.roles);
    return defaultRole;
  }

  function switchRole(newRole: string): void {
    if (!user.value?.roles?.includes(newRole)) {
      console.warn(`Tentativa de alternar para role não disponível: ${newRole}`);
      return;
    }

    activeRole.value = newRole;
    Cookies.set("active_role", newRole, { expires: 30 });
  }

  function resetRole(): void {
    activeRole.value = null;
    Cookies.remove("active_role");
  }

  function hasPermission(permission: string): boolean {
    if (isAdmin.value) return true;
    const role = activeRole.value || getDefaultRole();
    const roleDetail = user.value?.role_details?.find((detail) => detail.name === role);
    return roleDetail?.permissions.includes(permission) ?? false;
  }

  function canViewTrabalhos(): boolean {
    return hasPermission("trabalho.view") || isAdmin.value;
  }

  function canCreateTrabalho(): boolean {
    return hasPermission("trabalho.create") || isAdmin.value;
  }

  function canUpdateTrabalho(): boolean {
    return hasPermission("trabalho.update") || isAdmin.value;
  }

  function canDeleteTrabalho(): boolean {
    return hasPermission("trabalho.delete") || isAdmin.value;
  }

  function canViewCorrecoes(): boolean {
    return hasPermission("correcao.view") || isAdmin.value;
  }

  function canCreateCorrecao(): boolean {
    return hasPermission("correcao.create") || isAdmin.value;
  }

  function canResolveCorrecao(): boolean {
    return hasPermission("correcao.resolve") || isAdmin.value;
  }

  async function login(credentials: LoginCredentials): Promise<void> {
    try {
      isLoading.value = true;
      error.value = null;
      errorCode.value = null;

      const userData = await authService.login(credentials);
      setUser(userData);

      const defaultRole = getDefaultRole();
      switchRole(defaultRole);

      if (!userData.must_change_password) {
        const cursoStore = useCursoStore();
        await cursoStore.loadCursosUsuario();
      }
    } catch (err: any) {
      const errorMessage = err.message || "Erro ao fazer login. Tente novamente.";
      error.value = errorMessage;
      errorCode.value = err.code ?? null;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Limpa o estado local de sessao sem chamar a API. Usado tanto pelo
   * logout "feliz" quanto pelos catches internos de loadUser/initialize —
   * esses nunca podem repetir a chamada de logout, senao um 409 de
   * impersonacao (ver LogoutBloqueadoError) viraria rejeicao nao tratada
   * na inicializacao da aplicacao.
   */
  function limparSessaoLocal(): void {
    const cursoStore = useCursoStore();
    cursoStore.clearCurso();

    user.value = null;
    token.value = null;
    activeRole.value = null;
    Cookies.remove("auth_token");
    Cookies.remove("active_role");
    error.value = null;
    errorCode.value = null;
  }

  async function logout(): Promise<void> {
    isLoading.value = true;
    try {
      if (token.value) {
        await authService.logout();
      }
      limparSessaoLocal();
    } catch (err: any) {
      // O backend recusa o logout (409) quando ha uma sessao de "acessar
      // como outro usuario" ativa: encerrar as credenciais aqui derrubaria
      // a pessoa acessada de todos os dispositivos dela. Nao limpamos nada
      // localmente e propagamos a mensagem para a interface avisar.
      if (err?.response?.status === 409) {
        const message = err.response?.data?.message || "Encerre o acesso como outro usuário antes de sair.";
        throw new LogoutBloqueadoError(message);
      }

      console.error("Erro no logout:", err);
      limparSessaoLocal();
    } finally {
      isLoading.value = false;
    }
  }

  async function loadUser(): Promise<void> {
    if (!token.value) return;

    try {
      isLoading.value = true;
      const userData = await authService.getUser();
      user.value = userData;

      const savedActiveRole = Cookies.get("active_role");
      if (savedActiveRole && user.value?.roles?.includes(savedActiveRole)) {
        activeRole.value = savedActiveRole;
      } else {
        switchRole(getDefaultRole());
      }

      if (user.value?.id) {
        initialized.value = true;
      }

      if (!userData.must_change_password) {
        const cursoStore = useCursoStore();
        await cursoStore.loadCursosUsuario();
      }
    } catch (err) {
      console.error("Erro ao carregar usuário:", err);
      limparSessaoLocal();
    } finally {
      isLoading.value = false;
    }
  }

  async function initialize(): Promise<void> {
    if (initialized.value) return;

    try {
      isLoading.value = true;
      const savedToken = Cookies.get("auth_token");

      if (savedToken) {
        token.value = savedToken;
        await loadUser();
      }
    } catch (err) {
      console.error("Erro na inicialização:", err);
      limparSessaoLocal();
    } finally {
      isLoading.value = false;
      initialized.value = true;
    }
  }

  async function acessarComo(userId: number): Promise<void> {
    const { access_token } = await impersonacaoService.entrar(userId);

    // O perfil ativo do administrador nao pertence a conta da outra pessoa.
    // Sem zerar, CaptureActiveRole recusaria todas as rotas menos /auth/me.
    Cookies.remove("active_role");
    activeRole.value = null;

    // O token temporario dura 2h no backend (entrar() ja apagou a credencial
    // antiga do administrador) — o cookie tem que expirar junto, nao com a
    // validade de 7 dias de um login normal.
    Cookies.set("auth_token", access_token, {
      expires: new Date(Date.now() + DURACAO_TOKEN_IMPERSONACAO_HORAS * 60 * 60 * 1000),
      secure: process.env.NODE_ENV === "production",
      sameSite: "lax",
    });
    token.value = access_token;

    initialized.value = false;
    await loadUser();

    // loadUser() engole o proprio erro e so limpa o estado local — sem esta
    // checagem, um soluco em /auth/me logo apos entrar() resolveria como se
    // tivesse dado certo, com o cookie recem-gravado (a UNICA credencial
    // viva do administrador nesse instante) ja removido de novo.
    if (!user.value || !token.value) {
      throw new RecargaDeSessaoFalhouError(
        "Não foi possível carregar a sessão após acessar como outro usuário."
      );
    }
  }

  async function voltarAoMeuUsuario(): Promise<void> {
    const { access_token } = await impersonacaoService.voltar();

    Cookies.remove("active_role");
    activeRole.value = null;

    // Token normal do administrador (nao expira em 2h como o de
    // impersonacao) — mesma validade usada no login.
    Cookies.set("auth_token", access_token, {
      expires: 7,
      secure: process.env.NODE_ENV === "production",
      sameSite: "lax",
    });
    token.value = access_token;

    const cursoStore = useCursoStore();
    cursoStore.clearCurso();

    initialized.value = false;
    await loadUser();

    if (!user.value || !token.value) {
      throw new RecargaDeSessaoFalhouError(
        "Não foi possível carregar a sessão ao voltar ao seu usuário."
      );
    }
  }

  function clearError(): void {
    error.value = null;
    errorCode.value = null;
  }

  function setUser(userData: User | null): void {
    user.value = userData;
    if (userData?.id) {
      const savedToken = Cookies.get("auth_token");
      if (savedToken) {
        token.value = savedToken;
      }
      initialized.value = true;
    }
  }

  return {
    user,
    token,
    isLoading,
    error,
    errorCode,
    initialized,
    activeRole,
    availableRoles,
    hasMultipleRoles,
    activeRoleLabel,
    acessandoComo,
    nomeDoImpersonador,

    isAuthenticated,
    isAdmin,
    isAluno,
    isOrientador,
    isCoordenador,
    isSecretaria,
    isCoordenadorTcc,
    isUserNormal,

    hasRole,
    hasPermission,
    getDefaultRole,
    switchRole,
    resetRole,
    canViewTrabalhos,
    canCreateTrabalho,
    canUpdateTrabalho,
    canDeleteTrabalho,
    canViewCorrecoes,
    canCreateCorrecao,
    canResolveCorrecao,
    login,
    logout,
    loadUser,
    initialize,
    acessarComo,
    voltarAoMeuUsuario,
    clearError,
    setUser,
  };
});
